import type { PointerEvent as ReactPointerEvent } from "react";
import { useCallback, useRef, useState } from "react";

export interface ResizableColumn {
  key: string;
  defaultWidth: number;
  minWidth?: number;
}

const DEFAULT_MIN_WIDTH = 60;

function storageKeyFor(id: string): string {
  return `resizable-columns:${id}`;
}

function loadStoredWidths(id: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(storageKeyFor(id));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null) return {};
    const result: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "number" && Number.isFinite(value)) {
        result[key] = value;
      }
    }
    return result;
  } catch {
    return {};
  }
}

function initialWidths(id: string, columns: ResizableColumn[]): Record<string, number> {
  const stored = loadStoredWidths(id);
  const widths: Record<string, number> = {};
  for (const col of columns) {
    widths[col.key] = stored[col.key] ?? col.defaultWidth;
  }
  return widths;
}

export function useResizableColumns(id: string, columns: ResizableColumn[]) {
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  const [widths, setWidths] = useState<Record<string, number>>(() => initialWidths(id, columns));
  const widthsRef = useRef(widths);
  widthsRef.current = widths;

  const startResize = useCallback(
    (key: string) => (e: ReactPointerEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const column = columnsRef.current.find((c) => c.key === key);
      const minWidth = column?.minWidth ?? DEFAULT_MIN_WIDTH;

      // Measure the real rendered width rather than trusting stored/default
      // state, so a drag never starts with a jump.
      const th = (e.currentTarget as HTMLElement).parentElement;
      const startWidth =
        th?.getBoundingClientRect().width ??
        widthsRef.current[key] ??
        column?.defaultWidth ??
        minWidth;

      const prevCursor = document.body.style.cursor;
      const prevUserSelect = document.body.style.userSelect;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const onMove = (moveEvent: PointerEvent) => {
        const nextWidth = Math.max(minWidth, startWidth + (moveEvent.clientX - startX));
        setWidths((prev) => ({ ...prev, [key]: nextWidth }));
      };

      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = prevUserSelect;
        setWidths((prev) => {
          try {
            localStorage.setItem(storageKeyFor(id), JSON.stringify(prev));
          } catch {}
          return prev;
        });
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [id],
  );

  return { widths, startResize };
}
